import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  PieChart,
  Calendar,
  List,
  ArrowUpCircle,
  ArrowDownCircle,
  Wallet,
  ChevronLeft,
  ChevronRight,
  Plus,
  TrendingUp,
  TrendingDown,
  Search,
  LayoutGrid,
  RefreshCcw,
  ChevronDown,
  Save,
  ArrowLeft,
  Trash2,
  X,
  Check,
  BarChart3,
  Activity,
  Clock,
  Building2,
  ChevronRight as ChevronRightIcon,
  User,
  Truck,
  Landmark,
  CreditCard,
  Filter,
  Pencil,
  Percent,
  Eye,
  EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import { processPendingBankSettlements } from '../utils/bankSettlements';
import { fetchActiveOrganizationClientsWithBalances } from '../utils/clientBalances';
import { fetchActiveOrganizationProvidersWithBalances } from '../utils/providerBalances';
import { fetchAllPaginated } from '../utils/productCatalog';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import { parseLocalizedNumber } from '../utils/numberFormat';
import {
  getFlowMovements,
  groupByDay,
  groupByMonth,
  groupByEntity,
  summarize,
  formatMoney,
} from '../utils/flujoFondos';
import { useSite } from '../context/SiteContext';

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const toDateOnly = (value) => String(value || '').slice(0, 10);

const parseDateAtMidday = (value) => {
  const dateOnly = toDateOnly(value);
  if (!dateOnly) return null;
  const parsed = new Date(`${dateOnly}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const shiftDateOnly = (value, days = 0) => {
  const baseDate = parseDateAtMidday(value);
  if (!baseDate) return null;
  const nextDate = new Date(baseDate.getTime());
  nextDate.setDate(nextDate.getDate() + Number(days || 0));
  return nextDate.toISOString().slice(0, 10);
};

const addMonthsDateOnly = (value, months = 1) => {
  const dateOnly = toDateOnly(value);
  const parts = dateOnly.split('-').map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return null;
  const [year, month, day] = parts;
  const targetMonthIndex = month - 1 + Number(months || 0);
  const lastDay = new Date(year, targetMonthIndex + 1, 0, 12, 0, 0).getDate();
  const nextDate = new Date(year, targetMonthIndex, Math.min(day, lastDay), 12, 0, 0);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate.toISOString().slice(0, 10);
};

const buildRecurringDates = ({ startDate, frequency, untilDate }) => {
  const start = toDateOnly(startDate);
  const freq = String(frequency || 'unica').toLowerCase();
  if (!start || freq === 'unica') return start ? [start] : [];

  const until = toDateOnly(untilDate);
  if (!until || until < start) return [];

  const dates = [];
  let cursor = start;
  const maxOccurrences = 120;

  while (cursor && cursor <= until && dates.length < maxOccurrences) {
    dates.push(cursor);
    const prev = cursor;
    if (freq === 'semanal') cursor = shiftDateOnly(cursor, 7);
    else if (freq === 'quincenal') cursor = shiftDateOnly(cursor, 15);
    else if (freq === 'trimestral') cursor = addMonthsDateOnly(cursor, 3);
    else cursor = addMonthsDateOnly(cursor, 1);
    if (!cursor || cursor <= prev) break;
  }

  return dates;
};

const resolveExpenseCategory = (value) => {
  const normalized = normalizeText(value);
  if (normalized.includes('fijo')) return 'Fijo';
  if (normalized.includes('variable')) return 'Variable';
  return '';
};

const buildExpenseAnalysisGroups = (expenses = []) => {
  const categories = new Map();

  expenses.forEach((expense) => {
    const resolvedCategory = resolveExpenseCategory(expense?.categoria) || 'Sin categoria';
    const categoryLabel = resolvedCategory === 'Fijo'
      ? 'Fijos'
      : resolvedCategory === 'Variable'
        ? 'Variables'
        : 'Sin categoria';
    const conceptLabel = String(expense?.concepto || 'Gasto del sistema').trim() || 'Gasto del sistema';
    const conceptKey = normalizeText(conceptLabel) || conceptLabel.toLowerCase();
    const amount = Math.abs(Number(expense?.monto || 0)) || 0;

    if (!categories.has(categoryLabel)) {
      categories.set(categoryLabel, {
        id: resolvedCategory,
        label: categoryLabel,
        total: 0,
        itemsMap: new Map(),
      });
    }

    const categoryBucket = categories.get(categoryLabel);
    categoryBucket.total += amount;

    if (!categoryBucket.itemsMap.has(conceptKey)) {
      categoryBucket.itemsMap.set(conceptKey, {
        key: conceptKey,
        name: conceptLabel,
        total: 0,
        count: 0,
      });
    }

    const conceptBucket = categoryBucket.itemsMap.get(conceptKey);
    conceptBucket.total += amount;
    conceptBucket.count += 1;
  });

  return Array.from(categories.values())
    .map((group) => ({
      id: group.id,
      label: group.label,
      total: group.total,
      items: Array.from(group.itemsMap.values()).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
};

const FLOW_SECTION_TO_VIEW = {
  calendario: 'calendario',
  analisis: 'mes-actual',
  historial: 'tabla',
  bancos: 'bancos',
};

const resolveFlowSection = (value) => {
  const key = String(value || '').trim().toLowerCase();
  return FLOW_SECTION_TO_VIEW[key] ? key : 'calendario';
};

/**
 * Módulo: Flujo de Fondos
 * Objetivo: control de liquidez (real + proyectado) y drilldown mensual.
 * Refactorizado al estilo Productos.jsx
 */
export default function FlujoFondos({ initialSection = 'calendario' }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentSection = resolveFlowSection(initialSection);

  const pickerRef = useRef(null);
  const sitePickerRef = useRef(null);

  // --- ESTADOS ---
  const [viewMode, setViewMode] = useState(() => FLOW_SECTION_TO_VIEW[currentSection]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showSitePicker, setShowSitePicker] = useState(false);

  // Estado para acciones de proyección
  const [deletingId, setDeletingId] = useState(null);
  const [confirmingItem, setConfirmingItem] = useState(null);
  const [postponingId, setPostponingId] = useState(null);
  // ID crudo (sourceRowId) de la proyección a posponer. El motor único usa IDs
  // prefijados (`proyeccion:123`) para la UI/keys, pero la DB necesita el id real.
  const [postponeRowId, setPostponeRowId] = useState(null);
  const [newDatePostpone, setNewDatePostpone] = useState('');

  const [anio, setAnio] = useState(new Date().getFullYear());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [mesSeleccionado, setMesSeleccionado] = useState(null);
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());

  const [mesesData, setMesesData] = useState([]);
  const [dataMesCalendario, setDataMesCalendario] = useState({});

  // Saldo inicial acumulado histórico
  const [saldoInicialMes, setSaldoInicialMes] = useState(0);

  // Estados para Detalle de Banco
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankMovs, setBankMovs] = useState([]);
  const [bankFilter, setBankFilter] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const [banksList, setBanksList] = useState([]);
  const [searchMovs, setSearchMovs] = useState('');
  const [searchResumen, setSearchResumen] = useState('');
  const [expandedProviders, setExpandedProviders] = useState({});
  const [expandedClientes, setExpandedClientes] = useState({});
  // Movimientos crudos del mes en la vista Analisis. Los tres paneles se derivan
  // de aca con useMemo, para que el toggle de proyectado los recalcule sin tener
  // que volver a consultar la base.
  const [analisisMovements, setAnalisisMovements] = useState([]);
  // Toggle: incluir o no los importes proyectados (cuentas por cobrar,
  // vencimientos de compra, liquidaciones CRM y proyecciones manuales).
  // Por defecto apagado: se arranca mostrando solo lo realmente movido, que es
  // lo que cierra contra el Estado de Resultados.
  const [includeProjected, setIncludeProjected] = useState(false);
  const [focusedMovementId, setFocusedMovementId] = useState(null);

  const [formProj, setFormProj] = useState({
    concepto: '',
    monto: '',
    fecha: new Date().toISOString().split('T')[0],
    categoria: 'Fijo',
    frecuencia: 'unica',
    hasta: '',
    entidad_id: '',
  });
  // Edición de una proyección existente: { id, serieId } o null (alta nueva).
  const [editingProj, setEditingProj] = useState(null);

  const [sitios, setSitios] = useState([]);
  const [loadingSitios, setLoadingSitios] = useState(false);

  const [masters, setMasters] = useState({ banks: [], clients: [], providers: [] });
  const [loadingMasters, setLoadingMasters] = useState(false);

  const [confirmForm, setConfirmForm] = useState({
    bank_id: '',
    entity_id: '',
    obs: ''
  });

  // ✅ FIX bug #2 multi-sucursal (reactivo): consumimos el sitio del SiteContext
  // global. Antes esta página leía localStorage UNA vez al montar y NO escuchaba el
  // evento de cambio del Topbar, entonces los KPIs/calendario/listado quedaban
  // desactualizados al cambiar de sucursal sin recargar la página.
  const { selectedSiteId: contextSiteId, setSelectedSiteId: setContextSiteId } = useSite();
  const selectedSiteId = contextSiteId || null;
  const setSelectedSiteId = (next) => setContextSiteId(next || '');

  const safeNavigate = (path) => {
    try {
      navigate(path);
    } catch {
      window.location.href = path;
    }
  };

  const activateSection = (section) => {
    const nextSection = resolveFlowSection(section);
    const targetView = FLOW_SECTION_TO_VIEW[nextSection];

    if (currentSection === nextSection) {
      setViewMode(targetView);
      if (nextSection !== 'bancos') {
        setSelectedBank(null);
        setBankMovs([]);
      }
      if (nextSection !== 'historial') setMesSeleccionado(null);
      if (nextSection !== 'calendario') setFocusedMovementId(null);
      return;
    }

    safeNavigate(`/sistema/flujo-fondos/${nextSection}`);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) setShowDatePicker(false);
      if (sitePickerRef.current && !sitePickerRef.current.contains(event.target)) setShowSitePicker(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ FIX bug #2: la persistencia ahora la maneja SiteContext (org-scoped).
  // Eliminamos el efecto que escribía a localStorage manualmente para evitar que
  // dos fuentes peleen por la misma key.

  useEffect(() => {
    const dashboardFocus = location.state?.dashboardFocus;
    if (!dashboardFocus) return;

    if (dashboardFocus.type === 'vencimientos') {
      const focusDate = dashboardFocus.date ? new Date(`${dashboardFocus.date}T12:00:00`) : new Date();
      if (!Number.isNaN(focusDate.getTime())) {
        setCurrentDate(focusDate);
        setSelectedDay(focusDate.getDate());
      }
      setViewMode('calendario');
      setFocusedMovementId(dashboardFocus.movementId || null);
    }
  }, [location.state]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    fetchSitios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const isUUID = (v) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(v || '').trim());

  const resolveWritableSiteId = (siteId) => {
    const raw = String(siteId || '').trim();
    if (!raw) return null;

    const matchById = (sitios || []).find((site) => String(site?.id || '').trim() === raw);
    if (matchById?.id) return matchById.id;

    const matchByName = (sitios || []).find(
      (site) => normalizeText(site?.nombre) === normalizeText(raw)
    );
    if (matchByName?.id) return matchByName.id;

    if (!(sitios || []).length && isUUID(raw)) return raw;
    return null;
  };

  useEffect(() => {
    if (!sitios?.length) return;
    if (!selectedSiteId) return;

    const raw = String(selectedSiteId || '').trim();
    const resolvedSiteId = resolveWritableSiteId(raw);
    if (resolvedSiteId === raw) return;
    setSelectedSiteId(resolvedSiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitios]);

  useEffect(() => {
    const targetView = FLOW_SECTION_TO_VIEW[currentSection];
    setViewMode(targetView);

    if (currentSection !== 'bancos') {
      setSelectedBank(null);
      setBankMovs([]);
    }
    if (currentSection !== 'historial') setMesSeleccionado(null);
    if (currentSection !== 'calendario') setFocusedMovementId(null);
  }, [currentSection]);


  useEffect(() => {
    if (!profile?.organization_id) return;

    if (viewMode === 'tabla') {
      fetchAnualData();
    }
    else if (viewMode === 'calendario') {
      fetchSaldoAcumuladoPrevio();
      fetchCalendarioData();
    }
    else if (viewMode === 'detalle-mes' || viewMode === 'mes-actual') {
      fetchDetalleMensualAgregado();
    }
    else if (viewMode === 'bancos') {
      fetchBanksData();
    }
    else if (viewMode === 'detalle-banco' && selectedBank) {
      fetchBankMovements();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, anio, currentDate, mesSeleccionado, profile?.organization_id, selectedSiteId, selectedBank, bankFilter]);

  useEffect(() => {
    if (!focusedMovementId || viewMode !== 'calendario') return;
    const timeoutId = window.setTimeout(() => {
      const node = document.getElementById(`flujo-mov-${focusedMovementId}`);
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(timeoutId);
  }, [focusedMovementId, selectedDay, dataMesCalendario, viewMode]);

  // Al abrir el formulario de proyección cargamos las entidades (clientes /
  // proveedores) para el selector de vínculo opcional.
  useEffect(() => {
    if (viewMode === 'nuevo-ingreso-proy' || viewMode === 'nuevo-gasto-proy') {
      loadMastersData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // --- HELPERS ---
  const monthLabel = (date) => date.toLocaleString('es-AR', { month: 'long', year: 'numeric' });

  // Los limites del mes se arman como fecha LOCAL, no via toISOString().
  // toISOString() convierte a UTC, y en Argentina (UTC-3) el fin de mes a las
  // 23:59:59.999 cae en el dia 1 del mes siguiente: el rango de julio terminaba
  // en '2026-08-01'. Eso metia los movimientos del 1 de agosto dentro de julio,
  // los dibujaba en el dia 1 de la grilla (groupByDay lee los dos digitos del
  // dia) y hacia que ese dia se contara tambien en el mes anterior.
  const toLocalYMD = (d) => (
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  );

  const getMonthRange = (date) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0); // dia 0 del mes siguiente = ultimo del actual
    return {
      startYMD: toLocalYMD(start),
      endYMD: toLocalYMD(end),
    };
  };

  const normalizeMoney = (n) => {
    const v = Number(n || 0);
    return Number.isFinite(v) ? v : 0;
  };

  // --- DATA ---

  async function fetchSitios() {
    setLoadingSitios(true);
    try {
      const { data, error } = await supabase
        .from('sitios')
        .select('id, nombre, is_active')
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true)
        .order('nombre', { ascending: true });

      if (error) throw error;
      setSitios(data || []);
    } catch (e) {
      setSitios([]);
    } finally {
      setLoadingSitios(false);
    }
  }

  async function fetchBanksData() {
    setLoading(true);
    try {
      await processPendingBankSettlements(supabase, profile.organization_id);
      const { data, error } = await supabase
        .from('banks')
        .select('id, name, current_balance, currency, cbu_alias, initial_balance')
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setBanksList(data || []);
    } catch (e) {
      toast.error("Error al cargar bancos");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchBankMovements() {
    if (!selectedBank) return;
    setLoading(true);
    try {
      const fromISO = `${bankFilter.from}T00:00:00`;
      const toISO = `${bankFilter.to}T23:59:59`;

      const buildQuery = () => {
        let q = supabase
          .from('movimientos_financieros')
          .select('*')
          .eq('organization_id', profile.organization_id)
          .eq('bank_id', selectedBank.id)
          .gte('fecha_operacion', fromISO)
          .lte('fecha_operacion', toISO)
          .order('fecha_operacion', { ascending: false })
          .order('id', { ascending: false });

        if (selectedSiteId) {
          q = q.eq('sitio_id', selectedSiteId);
        }
        return q;
      };

      const data = await fetchAllPaginated(buildQuery);
      setBankMovs(data || []);

    } catch (e) {
      toast.error("Error cargando detalle bancario");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadMastersData() {
    if (masters.banks.length > 0) return;
    setLoadingMasters(true);
    try {
      const orgId = profile.organization_id;
      const [b, c, p] = await Promise.all([
        supabase.from('banks').select('id, name, current_balance').eq('organization_id', orgId).eq('is_active', true),
        fetchActiveOrganizationClientsWithBalances({ organizationId: orgId, supabaseClient: supabase }),
        fetchActiveOrganizationProvidersWithBalances({ organizationId: orgId, supabaseClient: supabase })
      ]);
      if (c.error) throw c.error;
      if (p.error) throw p.error;
      setMasters({
        banks: b.data || [],
        clients: c.data || [],
        providers: p.data || []
      });
    } catch (err) {
      console.error(err);
      toast.error("Error cargando entidades");
    } finally {
      setLoadingMasters(false);
    }
  }

  const selectedSiteLabel = useMemo(() => {
    if (!selectedSiteId) return 'Consolidado';
    const s = (sitios || []).find((x) => x.id === selectedSiteId);
    return s?.nombre || 'Sitio';
  }, [selectedSiteId, sitios]);

  // --- LÓGICA DE DATOS ---

  // CALCULO DE SALDO INICIAL (CORREGIDO PARA EVITAR DUPLICIDAD)
  async function fetchSaldoAcumuladoPrevio() {
    const orgId = profile.organization_id;
    const { startYMD } = getMonthRange(currentDate);
    const dayBefore = shiftDateOnly(startYMD, -1) || startYMD;

    try {
      const { data: banksData } = await supabase
        .from("banks")
        .select("initial_balance")
        .eq("organization_id", orgId)
        .eq("is_active", true);
      const initialBankSum = banksData?.reduce((acc, b) => acc + normalizeMoney(b.initial_balance), 0) || 0;

      // Saldo inicial = saldo base de bancos + neto de TODO lo anterior al mes,
      // calculado con el motor unico (mismas reglas que el calendario).
      const pastMovements = await getFlowMovements({
        supabaseClient: supabase,
        organizationId: orgId,
        siteId: selectedSiteId,
        fromDate: "2000-01-01",
        toDate: dayBefore,
      });

      setSaldoInicialMes(initialBankSum + summarize(pastMovements).net);
    } catch (e) {
      // No se asume 0 en silencio: se mantiene el valor previo y se avisa.
      console.error("Error calculando saldo inicial", e);
      toast.error("No se pudo calcular el saldo inicial del mes");
    }
  }

  async function fetchAnualData() {
    setLoading(true);
    try {
      // Motor unico: resumen anual = movimientos del año agrupados por mes.
      const movements = await getFlowMovements({
        supabaseClient: supabase,
        organizationId: profile.organization_id,
        siteId: selectedSiteId,
        fromDate: `${anio}-01-01`,
        toDate: `${anio}-12-31`,
      });
      setMesesData(groupByMonth(movements));
    } catch (e) {
      toast.error("Error en flujo anual");
    } finally {
      setLoading(false);
    }
  }

  async function fetchCalendarioData() {
    setLoading(true);
    try {
      const { startYMD, endYMD } = getMonthRange(currentDate);

      // Motor unico: una sola fuente de verdad para el mes.
      const movements = await getFlowMovements({
        supabaseClient: supabase,
        organizationId: profile.organization_id,
        siteId: selectedSiteId,
        fromDate: startYMD,
        toDate: endYMD,
      });

      setDataMesCalendario(groupByDay(movements));

      const daysInM = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
      if (selectedDay > daysInM) setSelectedDay(1);
    } catch (e) {
      toast.error(`Error en sincronización de calendario: ${e?.message || "desconocido"}`);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDetalleMensualAgregado() {
    let tAnio;
    let tMes;
    if (viewMode === "mes-actual") {
      tAnio = currentDate.getFullYear();
      tMes = currentDate.getMonth();
    } else {
      if (!mesSeleccionado) return;
      tAnio = mesSeleccionado.anio;
      tMes = mesSeleccionado.mes;
    }

    setLoading(true);
    setExpandedProviders({});
    setExpandedClientes({});
    // Los paneles se derivan de analisisMovements: limpiando esto se limpian los tres.
    setAnalisisMovements([]);

    const start = `${tAnio}-${(tMes + 1).toString().padStart(2, "0")}-01`;
    const end = `${tAnio}-${(tMes + 1).toString().padStart(2, "0")}-${new Date(tAnio, tMes + 1, 0).getDate()}`;

    try {
      // Motor unico: una sola fuente para el mes. Aca solo damos forma a los 3
      // paneles (Ingresos por cliente / Compras y Vencimientos / Gastos).
      const movements = await getFlowMovements({
        supabaseClient: supabase,
        organizationId: profile.organization_id,
        siteId: selectedSiteId,
        fromDate: start,
        toDate: end,
      });

      // Los tres paneles (ingresos, compras, gastos) se derivan de estos
      // movimientos con useMemo, para que el toggle de proyectado los recalcule
      // sin volver a pegarle a la base.
      setAnalisisMovements(movements);
      setExpandedProviders({});
    } catch (e) {
      toast.error("Error en análisis agregado");
    } finally {
      setLoading(false);
    }
  }

  const resetFormProyeccion = () => {
    setFormProj({
      concepto: '',
      monto: '',
      fecha: new Date().toISOString().split('T')[0],
      categoria: 'Fijo',
      frecuencia: 'unica',
      hasta: '',
      entidad_id: '',
    });
    setEditingProj(null);
  };

  const handleSaveProyeccion = async (tipo) => {
    const monto = parseLocalizedNumber(formProj.monto, { decimals: 2 });
    if (!formProj.concepto || !formProj.fecha) {
      return toast.error('Complete concepto y fecha');
    }
    if (!Number.isFinite(monto) || monto <= 0) return toast.error('Monto inválido');

    const entidadTipo = tipo === 'ingreso' ? 'Cliente' : 'Proveedor';
    const entidadId = formProj.entidad_id || null;

    setSaving(true);
    try {
      // --- EDICIÓN de una proyección existente (una ocurrencia) ---
      if (editingProj) {
        const { error } = await supabase
          .from('proyecciones')
          .update({
            concepto: String(formProj.concepto).trim().toUpperCase(),
            monto,
            fecha: formProj.fecha,
            categoria: formProj.categoria || 'Fijo',
            entidad_id: entidadId,
            entidad_tipo: entidadId ? entidadTipo : null,
          })
          .eq('id', editingProj.id);
        if (error) throw error;
        resetFormProyeccion();
        setViewMode('calendario');
        fetchCalendarioData();
        return toast.success('Proyección actualizada');
      }

      // --- ALTA (una o serie recurrente) ---
      const fechas = buildRecurringDates({
        startDate: formProj.fecha,
        frequency: formProj.frecuencia,
        untilDate: formProj.hasta,
      });
      if (!fechas.length) return toast.error('Indique una fecha final válida para la repetición');

      // Las ocurrencias de una serie comparten serie_id para poder editarla /
      // eliminarla como una unidad.
      const serieId = fechas.length > 1
        ? (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null)
        : null;

      const basePayload = {
        organization_id: profile.organization_id,
        concepto: String(formProj.concepto).trim().toUpperCase(),
        monto,
        tipo,
        sitio_id: resolveWritableSiteId(selectedSiteId),
        categoria: formProj.categoria || 'Fijo',
        entidad_id: entidadId,
        entidad_tipo: entidadId ? entidadTipo : null,
        serie_id: serieId,
      };
      const payloads = fechas.map((fecha) => ({ ...basePayload, fecha }));
      let { error } = await supabase.from('proyecciones').insert(payloads);
      let storedAsConsolidated = !basePayload.sitio_id;

      if (error && ['22P02', '23503'].includes(error.code)) {
        const retry = await supabase
          .from('proyecciones')
          .insert(payloads.map((payload) => ({ ...payload, sitio_id: null })));

        if (retry.error) throw retry.error;
        storedAsConsolidated = true;
        if (selectedSiteId) setSelectedSiteId(null);
        error = null;
      }

      if (error) throw error;
      const scope = storedAsConsolidated ? 'Consolidado' : 'Sitio';
      const successMessage = `${fechas.length} proyección${fechas.length === 1 ? '' : 'es'} registrada${fechas.length === 1 ? '' : 's'} (${scope})`;
      resetFormProyeccion();
      setViewMode('calendario');
      fetchCalendarioData();
      return toast.success(successMessage);
    } catch (e) {
      console.error('Error al guardar proyeccion:', e);
      return toast.error(e?.message || 'Fallo al persistir proyeccion');
    } finally {
      setSaving(false);
    }
  };

  const openEditProyeccion = (mov) => {
    setEditingProj({ id: mov.sourceRowId, serieId: mov.meta?.serieId || null });
    setFormProj({
      concepto: mov.concept || '',
      monto: mov.amount != null ? String(mov.amount) : '',
      fecha: mov.date,
      categoria: mov.meta?.categoria || 'Fijo',
      frecuencia: 'unica',
      hasta: '',
      entidad_id: mov.entityId || '',
    });
    loadMastersData();
    setViewMode(mov.type === 'in' ? 'nuevo-ingreso-proy' : 'nuevo-gasto-proy');
  };

  const handleDeleteProyeccion = async (mov) => {
    try {
      const serieId = mov?.meta?.serieId || null;
      const baseQuery = supabase.from('proyecciones').delete();
      const { error } = serieId
        ? await baseQuery.eq('serie_id', serieId).eq('organization_id', profile.organization_id)
        : await baseQuery.eq('id', mov?.sourceRowId ?? mov?.id ?? mov);
      if (error) throw error;
      toast.success(serieId ? 'Serie completa eliminada' : 'Proyección eliminada');
      setDeletingId(null);
      fetchCalendarioData();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const handleOpenConfirm = (item) => {
    setConfirmingItem(item);
    setConfirmForm({
      bank_id: '',
      // Si la proyección viene vinculada a una entidad, la precargamos para no
      // tener que volver a elegirla al confirmar.
      entity_id: item.entityId || '',
      obs: item.concept || ''
    });
    loadMastersData();
  };

  const handleExecuteConfirm = async () => {
    if (!confirmForm.bank_id) return toast.error("Seleccione la cuenta bancaria");
    if (!confirmForm.entity_id) return toast.error("Seleccione la entidad (Cliente/Proveedor)");

    setSaving(true);
    try {
      // ✅ FIX multi-sucursal: ya NO caemos silenciosamente a sitios[0]. Si el usuario no
      // tiene sitio seleccionado, guardamos null (Consolidado) y avisamos.
      const sitioId = selectedSiteId || null;
      if (!sitioId && Array.isArray(sitios) && sitios.length > 1) {
        toast('Sin sucursal seleccionada: el movimiento se guardará como Consolidado', { icon: 'ℹ️' });
      }

      // Confirmación TRANSACCIONAL en una sola operación (RPC Postgres).
      // Reemplaza las ~6 escrituras encadenadas que podían dejar estado
      // inconsistente ante un fallo, y evita las condiciones de carrera en los
      // saldos (la función usa incrementos atómicos). El cliente lo recalcula su
      // trigger; banco y proveedor se ajustan dentro de la misma transacción.
      const { error } = await supabase.rpc('confirmar_proyeccion_flujo', {
        p_payload: {
          organization_id: profile.organization_id,
          proyeccion_id: confirmingItem.sourceRowId ?? confirmingItem.id,
          tipo: confirmingItem.type, // 'in' (cobro) | 'out' (pago)
          monto: Number(confirmingItem.amount),
          bank_id: confirmForm.bank_id,
          entity_id: confirmForm.entity_id,
          fecha: confirmingItem.date,
          obs: confirmForm.obs || '',
          user_id: profile.id,
          sitio_id: sitioId,
        },
      });
      if (error) throw error;

      toast.success("MOVIMIENTO CONFIRMADO Y REGISTRADO");
      setConfirmingItem(null);
      fetchCalendarioData();

    } catch (e) {
      console.error(e);
      toast.error("Error al procesar la confirmación: " + (e?.message || e?.details || 'desconocido'));
    } finally {
      setSaving(false);
    }
  };

  const handleExecutePostpone = async () => {
    if (!newDatePostpone) return;
    try {
      await supabase.from('proyecciones').update({ fecha: newDatePostpone }).eq('id', postponeRowId ?? postponingId);
      toast.success("Fecha actualizada");
      setPostponingId(null);
      setPostponeRowId(null);
      setNewDatePostpone('');
      fetchCalendarioData();
    } catch (e) {
      toast.error("Error al posponer");
    }
  }


  // --- UI ---
  const MonthYearPicker = () => (
    <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 z-[100] animate-in fade-in zoom-in-95 duration-200">
      <div className="flex justify-between items-center mb-5">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setAnio(anio - 1);
          }}
          className="p-2 hover:bg-slate-50 rounded-lg transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-[11px] font-black text-slate-700 uppercase tracking-widest">{anio}</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setAnio(anio + 1);
          }}
          className="p-2 hover:bg-slate-50 rounded-lg transition-colors"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((m, idx) => (
          <button
            key={m}
            onClick={() => {
              setCurrentDate(new Date(anio, idx, 1));
              setShowDatePicker(false);
            }}
            className={`py-2.5 text-[9px] font-black uppercase rounded-xl transition-all ${currentDate.getMonth() === idx && currentDate.getFullYear() === anio ? 'bg-[#0B2A36] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'
              }`}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );

  const SitePicker = () => (
    <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 p-3 z-[110] animate-in fade-in zoom-in-95 duration-200">
      <button
        onClick={() => {
          setSelectedSiteId(null);
          setShowSitePicker(false);
        }}
        className={`w-full text-left px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${!selectedSiteId ? 'bg-[#0B2A36] text-white' : 'hover:bg-slate-50 text-slate-600'
          }`}
      >
        Consolidado
      </button>

      <div className="my-2 h-px bg-slate-100" />

      {loadingSitios ? (
        <div className="px-3 py-3 text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
          <RefreshCcw size={14} className="animate-spin" /> Cargando sitios...
        </div>
      ) : (
        <div className="max-h-64 overflow-y-auto custom-scrollbar pr-1">
          {(sitios || []).map((s) => (
            <button
              key={s.id}
              onClick={() => {
                setSelectedSiteId(s.id);
                setShowSitePicker(false);
              }}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${selectedSiteId === s.id ? 'bg-[#0B2A36] text-white' : 'hover:bg-slate-50 text-slate-600'
                }`}
              title={s.nombre}
            >
              {s.nombre}
            </button>
          ))}
          {(sitios || []).length === 0 && (
            <div className="px-3 py-3 text-[9px] font-black uppercase tracking-widest text-slate-400">No hay sitios activos</div>
          )}
        </div>
      )}
    </div>
  );

  const RenderBancos = () => (
    <div className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-300">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {banksList.map((bank) => (
          <div
            key={bank.id}
            onClick={() => {
              setSelectedBank(bank);
              setViewMode('detalle-banco');
            }}
            className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between hover:border-blue-200 cursor-pointer transition-all h-full group"
          >
            <div>
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl group-hover:bg-indigo-100 transition-colors">
                  <Landmark size={20} />
                </div>
                <span className="text-[10px] font-black uppercase bg-slate-100 text-slate-500 px-2 py-1 rounded-md tracking-wider">{bank.currency}</span>
              </div>
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-1">{bank.name}</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest truncate mb-4">
                {bank.cbu_alias || 'Sin CBU/Alias'}
              </p>
            </div>
            <div>
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Saldo Actual</p>
              <p className={`text-xl font-black tracking-tighter ${Number(bank.current_balance) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                $ {Number(bank.current_balance).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        ))}

        {banksList.length === 0 && (
          <div className="col-span-full h-40 flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-2xl text-slate-400">
            <Landmark size={30} className="mb-2 opacity-50" />
            <p className="text-[10px] font-black uppercase tracking-widest">No hay cuentas bancarias activas</p>
          </div>
        )}
      </div>
    </div>
  );

  const RenderDetalleBanco = () => {
    if (!selectedBank) return null;

    return (
      <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setViewMode('bancos')} className="p-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
              <ArrowLeft size={18} className="text-slate-600" />
            </button>
            <div>
              <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight leading-none">{selectedBank.name}</h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Movimientos y Auditoría</p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-white p-1.5 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg">
              <span className="text-[9px] font-black text-slate-400 uppercase">Desde</span>
              <input
                type="date"
                value={bankFilter.from}
                onChange={(e) => setBankFilter({ ...bankFilter, from: e.target.value })}
                className="bg-transparent text-[10px] font-bold outline-none uppercase text-slate-700 w-24"
              />
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg">
              <span className="text-[9px] font-black text-slate-400 uppercase">Hasta</span>
              <input
                type="date"
                value={bankFilter.to}
                onChange={(e) => setBankFilter({ ...bankFilter, to: e.target.value })}
                className="bg-transparent text-[10px] font-bold outline-none uppercase text-slate-700 w-24"
              />
            </div>
            <button
              onClick={fetchBankMovements}
              className="p-2 bg-[#0B2A36] text-white rounded-lg hover:brightness-110 transition-all"
              title="Aplicar filtro"
            >
              <Filter size={14} />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
              <Wallet size={24} />
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Saldo Actual Real</p>
              <h3 className={`text-2xl font-black tracking-tighter ${selectedBank.current_balance >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                $ {Number(selectedBank.current_balance).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">{selectedBank.currency}</p>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{selectedBank.cbu_alias}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
          <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Historial de Operaciones</h3>
            <span className="text-[9px] font-black uppercase text-slate-400 bg-white border border-slate-200 px-2 py-1 rounded">
              {bankMovs.length.toLocaleString('es-AR')} Registros
            </span>
          </div>
          <div className="overflow-x-auto imp-table-wrap">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="px-5 py-3">Fecha</th>
                  <th className="px-5 py-3">Tipo</th>
                  <th className="px-5 py-3">Descripción</th>
                  <th className="px-5 py-3 text-right">Monto</th>
                  <th className="px-5 py-3 text-right">Saldo Resultante</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {bankMovs.map((mov) => (
                  <tr key={mov.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3 text-[10px] font-bold text-slate-600">
                      {new Date(mov.fecha_operacion).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-[9px] font-black uppercase px-2 py-1 rounded ${mov.tipo === 'Ingreso' ? 'bg-blue-50 text-blue-600' : 'bg-rose-50 text-rose-600'
                        }`}>
                        {mov.tipo}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-bold text-slate-700 uppercase truncate max-w-[200px]">{mov.descripcion}</p>
                      <p className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">{mov.categoria}</p>
                    </td>
                    <td className={`px-5 py-3 text-right text-[11px] font-black ${mov.tipo === 'Ingreso' ? 'text-emerald-500' : 'text-rose-500'
                      }`}>
                      {mov.tipo === 'Ingreso' ? '+' : '-'} $ {Number(mov.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-5 py-3 text-right text-[10px] font-bold text-slate-500">
                      $ {Number(mov.saldo_resultante || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
                {bankMovs.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-20 text-center text-[10px] font-black text-slate-300 uppercase tracking-widest">
                      No hay movimientos en este rango de fechas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const RenderFormProyeccion = (tipo) => {
    const isEditing = !!editingProj;
    const entityList = tipo === 'ingreso' ? (masters.clients || []) : (masters.providers || []);
    const entityLabel = tipo === 'ingreso' ? 'Cliente' : 'Proveedor';
    const getEntityName = (e) => (tipo === 'ingreso' ? e?.full_name : e?.razon_social) || 'Sin nombre';
    // Preview de la serie recurrente (solo en alta).
    const serieDates = (!isEditing && formProj.frecuencia !== 'unica' && formProj.fecha && formProj.hasta)
      ? buildRecurringDates({ startDate: formProj.fecha, frequency: formProj.frecuencia, untilDate: formProj.hasta })
      : [];

    return (
    <div className="max-w-3xl w-full mx-auto animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 bg-[#0B2A36] text-white relative">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => { resetFormProyeccion(); setViewMode('calendario'); }} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
                <ArrowLeft size={18} />
              </button>
              <div>
                <h2 className="text-sm font-black uppercase tracking-tight">
                  {isEditing ? `Editar proyección de ${tipo}` : `Proyección de ${tipo}`}
                </h2>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">
                  Alcance: <span className="text-white">{selectedSiteLabel}</span>
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 opacity-90">
              {tipo === 'ingreso' ? <ArrowUpCircle size={20} className="text-[#51A7FD]" /> : <ArrowDownCircle size={20} className="text-[#F05A57]" />}
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          <div className="space-y-2">
            <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] ml-1">Referencia / Concepto</label>
            <input
              type="text"
              placeholder="EJ: COBRO FACTURA X, PAGO ALQUILER..."
              value={formProj.concepto}
              onChange={(e) => setFormProj({ ...formProj, concepto: e.target.value })}
              className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black uppercase outline-none focus:border-[#51A7FD] shadow-inner transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] ml-1">Monto Estimado</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                <LocalizedNumberInput
                  decimals={2}
                  selectOnFocus
                  placeholder="0,00"
                  value={formProj.monto}
                  onValueChange={(display) => setFormProj({ ...formProj, monto: display })}
                  className={`w-full pl-8 pr-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-sm font-black outline-none shadow-inner transition-all ${tipo === 'ingreso' ? 'text-blue-600' : 'text-rose-600'
                    }`}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] ml-1">Fecha Estimada</label>
              <input
                type="date"
                value={formProj.fecha}
                onChange={(e) => setFormProj({ ...formProj, fecha: e.target.value })}
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black outline-none shadow-inner cursor-pointer"
              />
            </div>
          </div>

          {/* Vínculo opcional a entidad: precarga el cliente/proveedor al confirmar */}
          <div className="space-y-2">
            <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] ml-1">
              {entityLabel} <span className="text-slate-300 normal-case">(opcional)</span>
            </label>
            <select
              value={formProj.entidad_id}
              onChange={(e) => setFormProj({ ...formProj, entidad_id: e.target.value })}
              className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
            >
              <option value="">{loadingMasters ? 'Cargando...' : `Sin ${entityLabel.toLowerCase()} vinculado`}</option>
              {entityList.map((e) => (
                <option key={e.id} value={e.id}>{getEntityName(e)}</option>
              ))}
            </select>
          </div>

          {!isEditing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] ml-1">Repeticion</label>
                <select
                  value={formProj.frecuencia}
                  onChange={(e) => setFormProj({ ...formProj, frecuencia: e.target.value, hasta: e.target.value === 'unica' ? '' : formProj.hasta })}
                  className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
                >
                  <option value="unica">Una vez</option>
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                  <option value="trimestral">Trimestral</option>
                </select>
              </div>

              {formProj.frecuencia !== 'unica' && (
                <div className="space-y-2">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] ml-1">Repetir hasta</label>
                  <input
                    type="date"
                    value={formProj.hasta}
                    min={formProj.fecha}
                    onChange={(e) => setFormProj({ ...formProj, hasta: e.target.value })}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black outline-none shadow-inner cursor-pointer"
                  />
                </div>
              )}
            </div>
          )}

          {/* Preview de la serie recurrente */}
          {!isEditing && formProj.frecuencia !== 'unica' && (
            <div className={`rounded-xl px-4 py-3 text-[10px] font-bold uppercase tracking-widest border ${serieDates.length ? 'bg-blue-50/60 border-blue-100 text-blue-600' : 'bg-amber-50/60 border-amber-100 text-amber-600'}`}>
              {serieDates.length ? (
                <>
                  Se crearán <b>{serieDates.length}</b> ocurrencia{serieDates.length === 1 ? '' : 's'}, hasta el{' '}
                  {new Date(`${serieDates[serieDates.length - 1]}T12:00:00`).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}.
                  {serieDates.length >= 120 && <span className="block mt-1 text-rose-500">Tope de 120 ocurrencias alcanzado; acortá el rango si necesitás más.</span>}
                </>
              ) : (
                'Indicá una fecha "Repetir hasta" posterior a la fecha estimada para generar la serie.'
              )}
            </div>
          )}

          {isEditing && (
            <div className="rounded-xl px-4 py-3 text-[10px] font-bold uppercase tracking-widest bg-slate-50 border border-slate-100 text-slate-500">
              Editando una ocurrencia.{editingProj?.serieId ? ' Forma parte de una serie; el resto no se modifica.' : ''}
            </div>
          )}

          {tipo === 'egreso' && (
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] ml-1">Categoría de Gasto</label>
              <select
                value={formProj.categoria}
                onChange={(e) => setFormProj({ ...formProj, categoria: e.target.value })}
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black uppercase outline-none focus:border-[#F05A57] shadow-inner cursor-pointer"
              >
                <option value="Fijo">Gasto Fijo</option>
                <option value="Variable">Gasto Variable</option>
              </select>
            </div>
          )}

          <button
            onClick={() => handleSaveProyeccion(tipo)}
            disabled={saving}
            className="w-full py-4 rounded-xl text-[#0B2A36] font-black text-[10px] uppercase tracking-[0.3em] shadow-sm transition-all flex items-center justify-center gap-3 bg-[#3ff2aa] hover:brightness-105 active:scale-[0.99]"
          >
            {saving ? <RefreshCcw size={16} className="animate-spin" /> : <Save size={16} />} {isEditing ? 'Guardar cambios' : 'Guardar proyección'}
          </button>
        </div>
      </div>
    </div>
    );
  };

  const RenderCalendario = () => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const daysInM = new Date(y, m + 1, 0).getDate();
    const startIdx = new Date(y, m, 1).getDay();

    const calendarDays = Array(startIdx)
      .fill(null)
      .concat([...Array(daysInM).keys()].map((i) => i + 1));

    const saldosAcumulados = {};
    let saldoRunning = saldoInicialMes;

    for (let d = 1; d <= daysInM; d++) {
      const info = dataMesCalendario[d] || { ingresos: 0, egresos: 0, ingProj: 0, gasProj: 0 };
      const delDia = normalizeMoney(info.ingresos) + normalizeMoney(info.ingProj) - (normalizeMoney(info.egresos) + normalizeMoney(info.gasProj));
      saldoRunning += delDia;
      saldosAcumulados[d] = saldoRunning;
    }

    const dayInfo = dataMesCalendario[selectedDay] || { ingresos: 0, egresos: 0, ingProj: 0, gasProj: 0, movs: [] };
    const dIn = normalizeMoney(dayInfo.ingresos) + normalizeMoney(dayInfo.ingProj);
    const dOut = normalizeMoney(dayInfo.egresos) + normalizeMoney(dayInfo.gasProj);

    const filteredMovs = dayInfo.movs.filter((mov) => {
      const q = searchMovs.trim().toLowerCase();
      if (!q) return true;
      return `${mov.concept || ''} ${mov.category || ''}`.toLowerCase().includes(q);
    });

    return (
      <div className="space-y-3 animate-in fade-in slide-in-from-left-4 duration-300">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
          <div className="xl:col-span-8 space-y-3">
            <div className="grid grid-cols-7 gap-2">
              {['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'].map((d) => (
                <div key={d} className="text-center text-[9px] font-black text-slate-400 uppercase tracking-widest pb-1">
                  {d}
                </div>
              ))}

              {calendarDays.map((day, idx) => {
                if (!day) return <div key={`empty-${idx}`} className="h-20 md:h-24 rounded-2xl border border-dashed border-slate-100 opacity-20" />;

                const info = dataMesCalendario[day] || { ingresos: 0, egresos: 0, ingProj: 0, gasProj: 0 };
                const isS = selectedDay === day;
                const saldoDelDia = saldosAcumulados[day];
                const hasProj = normalizeMoney(info.ingProj) > 0 || normalizeMoney(info.gasProj) > 0;

                return (
                  <div
                    key={day}
                    onClick={() => {
                      setSelectedDay(day);
                      setFocusedMovementId(null);
                    }}
                    className={`h-20 md:h-24 p-2 rounded-xl border transition-all cursor-pointer flex flex-col justify-between group relative ${isS ? 'bg-[#0B2A36] border-[#0B2A36] text-white shadow-lg scale-[1.02] z-10' : 'bg-white border-slate-100 hover:border-[#51A7FD] shadow-sm'
                      }`}
                  >
                    <div className="flex justify-between items-start">
                      <span className={`text-sm md:text-base font-black ${isS ? 'text-white' : 'text-slate-500'}`}>{day}</span>
                      {hasProj && <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse border border-white/50" />}
                    </div>
                    <div className="text-right">
                      <p className={`text-[6px] font-black uppercase tracking-tighter ${isS ? 'text-slate-400' : 'text-slate-300'}`}>Caja</p>
                      <p className={`text-[9px] md:text-[10px] font-black tracking-tight leading-tight ${isS ? 'text-[#3ff2aa]' : saldoDelDia >= 0 ? 'text-[#51A7FD]' : 'text-rose-500'}`}>
                        {formatMoney(saldoDelDia)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <aside className="xl:col-span-4">
            <div className="bg-white rounded-2xl flex flex-col p-0 overflow-hidden border border-slate-200 h-[500px] xl:h-[600px] shadow-sm sticky top-4">
              <div className="p-4 border-b border-slate-100 bg-[#0B2A36] text-white">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Activity size={16} className="text-[#3FF2AA]" />
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em]">Movimientos</h3>
                  </div>
                  <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    {selectedDay} de {monthLabel(currentDate)}
                  </div>
                </div>

                <div className="mt-3">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
                    <input
                      value={searchMovs}
                      onChange={(e) => setSearchMovs(e.target.value)}
                      placeholder="Buscar movimientos..."
                      className="w-full bg-white/10 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-[10px] font-black uppercase tracking-widest placeholder:text-slate-500 outline-none focus:border-white/20"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                {filteredMovs.map((mov, i) => (
                  <div
                    key={`${mov.id}-${i}`}
                    id={`flujo-mov-${mov.id}`}
                    className={`flex flex-col p-3 rounded-xl border transition-all ${mov.id === focusedMovementId
                        ? 'border-[#51A7FD] bg-blue-50 shadow-md ring-1 ring-[#51A7FD]/20'
                        : mov.isProj
                          ? 'border-dashed border-slate-300 bg-slate-50'
                          : 'border-slate-50 bg-white shadow-sm'
                      }`}
                  >

                    {/* CONFIRMACIÓN DE PROYECCIÓN - UI INLINE */}
                    {confirmingItem && confirmingItem.id === mov.id ? (
                      <div className="space-y-3 animate-in fade-in zoom-in-95">
                        <div className="flex justify-between items-center border-b border-slate-200 pb-2 mb-2">
                          <span className="text-[9px] font-black uppercase text-[#0B2A36] tracking-widest">Confirmar {mov.type === 'in' ? 'Cobro' : 'Pago'} Real</span>
                          <button onClick={() => setConfirmingItem(null)} className="text-slate-400 hover:text-rose-500"><X size={14} /></button>
                        </div>

                        <div className="space-y-2">
                          {loadingMasters ? <p className="text-[8px] italic text-slate-400">Cargando...</p> : (
                            <>
                              <select
                                className="w-full bg-white border border-slate-200 p-1.5 rounded text-[9px] font-bold uppercase outline-none"
                                value={confirmForm.entity_id}
                                onChange={e => setConfirmForm({ ...confirmForm, entity_id: e.target.value })}
                              >
                                <option value="">{mov.type === 'in' ? 'SELECCIONE CLIENTE...' : 'SELECCIONE PROVEEDOR...'}</option>
                                {mov.type === 'in'
                                  ? masters.clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)
                                  : masters.providers.map(p => <option key={p.id} value={p.id}>{p.razon_social}</option>)
                                }
                              </select>
                              <select
                                className="w-full bg-white border border-slate-200 p-1.5 rounded text-[9px] font-bold uppercase outline-none"
                                value={confirmForm.bank_id}
                                onChange={e => setConfirmForm({ ...confirmForm, bank_id: e.target.value })}
                              >
                                <option value="">CUENTA BANCARIA / CAJA...</option>
                                {masters.banks.map(b => <option key={b.id} value={b.id}>{b.name} (${b.current_balance})</option>)}
                              </select>
                              <textarea
                                placeholder="Observaciones adicionales..."
                                className="w-full bg-white border border-slate-200 p-1.5 rounded text-[9px] uppercase outline-none h-14"
                                value={confirmForm.obs}
                                onChange={e => setConfirmForm({ ...confirmForm, obs: e.target.value })}
                              />
                            </>
                          )}
                        </div>
                        <button
                          onClick={handleExecuteConfirm}
                          disabled={saving}
                          className="w-full bg-[#3ff2aa] hover:brightness-105 text-[#0B2A36] py-2 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
                        >
                          {saving ? <RefreshCcw className="animate-spin" size={12} /> : <Check size={12} />} Procesar Movimiento
                        </button>
                      </div>
                    ) : deletingId === mov.id ? (
                      <div className="flex items-center justify-between w-full animate-in zoom-in-95 duration-200">
                        <span className="text-[8px] font-black text-rose-500 uppercase tracking-widest">{mov.meta?.serieId ? '¿Borrar TODA la serie?' : '¿Confirmar borrado?'}</span>
                        <div className="flex gap-2">
                          <button onClick={() => handleDeleteProyeccion(mov)} className="p-1.5 bg-rose-500 text-white rounded-lg">
                            <Check size={14} />
                          </button>
                          <button onClick={() => setDeletingId(null)} className="p-1.5 bg-slate-100 text-slate-400 rounded-lg">
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ) : postponingId === mov.id ? (
                      <div className="animate-in fade-in slide-in-from-top-2 w-full">
                        <div className="flex gap-2 items-center">
                          <input type="date" value={newDatePostpone} onChange={e => setNewDatePostpone(e.target.value)} className="bg-white border border-slate-200 text-[10px] rounded p-1 w-full" />
                          <button onClick={handleExecutePostpone} className="bg-blue-500 text-white p-1 rounded"><Check size={14} /></button>
                          <button onClick={() => setPostponingId(null)} className="bg-slate-200 text-slate-500 p-1 rounded"><X size={14} /></button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-2 rounded-lg shrink-0 ${mov.type === 'in' ? 'bg-blue-50 text-blue-500' : 'bg-rose-50 text-rose-500'}`}>
                            {mov.isProj ? <Clock size={14} /> : mov.type === 'in' ? <ArrowUpCircle size={14} /> : <ArrowDownCircle size={14} />}
                          </div>
                          <div className="truncate">
                            <p className="text-[10px] font-black text-slate-700 uppercase truncate leading-tight">{mov.concept}</p>
                            <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">{mov.category}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-black ${mov.type === 'in' ? 'text-blue-600' : 'text-rose-600'}`}>{formatMoney(mov.amount)}</span>

                          {mov.isProj && mov.isManualProjection !== false && (
                            <div className="flex gap-0.5 ml-1">
                              <button onClick={() => handleOpenConfirm(mov)} className="p-1 text-slate-300 hover:text-emerald-500 transition-colors" title="Confirmar (Hacer Real)">
                                <Check size={12} strokeWidth={3} />
                              </button>
                              <button onClick={() => openEditProyeccion(mov)} className="p-1 text-slate-300 hover:text-amber-500 transition-colors" title="Editar proyección">
                                <Pencil size={12} />
                              </button>
                              <button onClick={() => { setPostponingId(mov.id); setPostponeRowId(mov.sourceRowId ?? mov.id); }} className="p-1 text-slate-300 hover:text-blue-500 transition-colors" title="Posponer">
                                <Clock size={12} />
                              </button>
                              <button onClick={() => setDeletingId(mov.id)} className="p-1 text-slate-300 hover:text-rose-400 transition-colors" title="Eliminar proyección">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {filteredMovs.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center opacity-20 py-10 grayscale">
                    <Search size={32} strokeWidth={1} />
                    <p className="text-[8px] font-black uppercase tracking-widest mt-2">Sin operaciones</p>
                  </div>
                )}
              </div>

              <div className="p-4 bg-slate-50 border-t border-slate-100 shrink-0">
                <div className="space-y-1 mb-3">
                  <div className="flex justify-between items-center opacity-60">
                    <span className="text-[8px] font-black uppercase tracking-widest">Ingresos Totales</span>
                    <span className="text-[10px] font-black text-blue-600">{formatMoney(dIn)}</span>
                  </div>
                  <div className="flex justify-between items-center opacity-60">
                    <span className="text-[8px] font-black uppercase tracking-widest">Egresos Totales</span>
                    <span className="text-[10px] font-black text-rose-600">{formatMoney(dOut)}</span>
                  </div>
                </div>
                <p className="text-[7px] font-black uppercase text-slate-400 tracking-widest mb-1 italic">Balance Neto del Día</p>
                <h4 className={`text-xl font-black tracking-tighter ${dIn - dOut < 0 ? 'text-rose-600' : 'text-emerald-500'}`}>{formatMoney(dIn - dOut)}</h4>
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  };

  const RenderTablaAnual = () => (
    <div className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-300">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto imp-table-wrap">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-6 py-4">Mes Operativo</th>
                <th className="px-6 py-4 text-right">Ingresos (+)</th>
                <th className="px-6 py-4 text-right">Egresos (-)</th>
                <th className="px-6 py-4 text-right">Flujo Neto</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-sans">
              {mesesData.map((m) => (
                <tr
                  key={m.mes}
                  onClick={() => {
                    setMesSeleccionado({ anio: anio, mes: m.mes });
                    setViewMode('detalle-mes');
                  }}
                  className="hover:bg-blue-50/20 transition-all cursor-pointer group"
                  title="Ver análisis resumido del mes"
                >
                  <td className="px-6 py-4 text-xs font-black text-slate-700 uppercase tracking-tight">{m.nombre}</td>
                  <td className="px-6 py-4 text-right text-xs font-black text-[#51A7FD]">$ {Number(m.ingresos || 0).toLocaleString('es-AR')}</td>
                  <td className="px-6 py-4 text-right text-xs font-black text-rose-500">$ {Number(m.egresos || 0).toLocaleString('es-AR')}</td>
                  <td className={`px-6 py-4 text-right text-sm font-black tracking-tight ${m.ingresos - m.egresos >= 0 ? 'text-emerald-500' : 'text-rose-600'}`}>
                    $ {Number(m.ingresos - m.egresos).toLocaleString('es-AR')}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <ChevronRightIcon size={18} className="text-slate-200 group-hover:text-[#51A7FD] inline transition-all" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-2">Tip: click en un mes para ver el análisis de ingresos y egresos por tipo.</div>
    </div>
  );

  const RenderDetalleMensualAgregado = () => {
    const q = searchResumen.trim().toLowerCase();
    const isDrilldown = viewMode === 'detalle-mes';

    const groupedExpenses = (gastosAnalisis || [])
      .map((group) => {
        const items = (group.items || []).filter((item) => {
          if (!q) return true;
          return `${group.label} ${item.name}`.toLowerCase().includes(q);
        });

        return {
          ...group,
          items,
          totalFiltered: items.reduce((acc, item) => acc + normalizeMoney(item.total), 0),
        };
      })
      .filter((group) => group.items.length > 0);

    return (
      <div className="space-y-4 animate-in fade-in zoom-in-95">
        {isDrilldown && (
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setViewMode('tabla')} className="p-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
              <ArrowLeft size={18} className="text-slate-600" />
            </button>
            <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">
              Análisis de {mesesData[mesSeleccionado?.mes]?.nombre || 'Mes'} {mesSeleccionado?.anio}
            </h2>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Distribución mensual y gastos del sistema</div>
          <div className="relative w-full sm:w-80">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
            <input
              value={searchResumen}
              onChange={(e) => setSearchResumen(e.target.value)}
              placeholder="Buscar cliente / concepto..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-[10px] font-black uppercase tracking-widest placeholder:text-slate-400 outline-none focus:border-[#51A7FD]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pb-2">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[450px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-blue-50/50">
              <h3 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Fuentes de Ingreso</h3>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-black text-blue-400 bg-blue-50 px-2 py-0.5 rounded-full">{ingresosClientes.length} fuentes</span>
                <TrendingUp size={16} className="text-blue-400 opacity-40" />
              </div>
            </div>
            <div className="p-4 space-y-2 flex-1 overflow-y-auto custom-scrollbar">
              {ingresosClientes.length > 0 ? (
                ingresosClientes
                  .filter(cli => !q || cli.name.toLowerCase().includes(q))
                  .map((cli) => {
                    const isOpen = !!expandedClientes[cli.id];
                    return (
                      <div key={cli.id} className="rounded-2xl border border-blue-100 overflow-hidden transition-all">
                        <button
                          onClick={() => setExpandedClientes(prev => ({ ...prev, [cli.id]: !prev[cli.id] }))}
                          className="w-full flex justify-between items-center px-4 py-3 bg-blue-50/60 hover:bg-blue-50 transition-colors group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-1.5 bg-blue-100 rounded-lg shrink-0">
                              <User size={13} className="text-blue-500" />
                            </div>
                            <span className="text-[10px] font-black text-slate-700 uppercase truncate">{cli.name}</span>
                            <span className="text-[9px] font-bold text-slate-400 shrink-0">{cli.ventas.length} registro{cli.ventas.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-[11px] font-black text-blue-600 tracking-tight">+ $ {Number(cli.total).toLocaleString('es-AR')}</span>
                            <ChevronDown size={14} className={`text-blue-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="divide-y divide-blue-50 bg-white animate-in fade-in slide-in-from-top-1 duration-200">
                            {cli.ventas.map((v) => (
                              <div key={v.id} className="flex items-center justify-between px-5 py-2.5 hover:bg-blue-50/30 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-1.5 h-1.5 rounded-full bg-blue-300 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[9px] font-black text-slate-600 uppercase tracking-wide">
                                      {new Date(v.fecha + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </p>
                                    {v.label && (
                                      <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest truncate">{v.label}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-[10px] font-black text-blue-500 shrink-0 ml-3">$ {Number(v.amount).toLocaleString('es-AR')}</span>
                              </div>
                            ))}
                            <div className="flex justify-between items-center px-5 py-2 bg-blue-50/50">
                              <span className="text-[8px] font-black text-blue-400 uppercase tracking-widest">Total {cli.name}</span>
                              <span className="text-[10px] font-black text-blue-600">$ {Number(cli.total).toLocaleString('es-AR')}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
              ) : (
                <div className="h-full flex flex-col items-center justify-center opacity-20 gap-3 py-10">
                  <RefreshCcw size={40} />
                  <p className="text-[10px] font-black uppercase tracking-widest">Sin ingresos en el período</p>
                </div>
              )}
            </div>
          </div>


          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[450px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-rose-50/50">
              <h3 className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Compras y Vencimientos</h3>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-black text-rose-400 bg-rose-50 px-2 py-0.5 rounded-full">{egresosProveedores.length} proveedores</span>
                <TrendingDown size={16} className="text-rose-400 opacity-40" />
              </div>
            </div>
            <div className="p-4 space-y-2 flex-1 overflow-y-auto custom-scrollbar">
              {egresosProveedores.length > 0 ? (
                egresosProveedores
                  .filter(prov => !q || prov.name.toLowerCase().includes(q))
                  .map((prov) => {
                    const isOpen = !!expandedProviders[prov.id];
                    return (
                      <div key={prov.id} className="rounded-2xl border border-rose-100 overflow-hidden transition-all">
                        {/* Cabecera del proveedor */}
                        <button
                          onClick={() => setExpandedProviders(prev => ({ ...prev, [prov.id]: !prev[prov.id] }))}
                          className="w-full flex justify-between items-center px-4 py-3 bg-rose-50/60 hover:bg-rose-50 transition-colors group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-1.5 bg-rose-100 rounded-lg shrink-0">
                              <Truck size={13} className="text-rose-500" />
                            </div>
                            <span className="text-[10px] font-black text-slate-700 uppercase truncate">{prov.name}</span>
                            <span className="text-[9px] font-bold text-slate-400 shrink-0">{prov.compras.length} compra{prov.compras.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-[11px] font-black text-rose-600 tracking-tight">- $ {Number(prov.total).toLocaleString('es-AR')}</span>
                            <ChevronDown size={14} className={`text-rose-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        {/* Detalle de compras por fecha */}
                        {isOpen && (
                          <div className="divide-y divide-rose-50 bg-white animate-in fade-in slide-in-from-top-1 duration-200">
                            {prov.compras.map((c) => (
                              <div key={c.id} className="flex items-center justify-between px-5 py-2.5 hover:bg-rose-50/30 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-1.5 h-1.5 rounded-full bg-rose-300 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[9px] font-black text-slate-600 uppercase tracking-wide">
                                      Vence {new Date(`${c.fechaVencimiento || c.fecha}T12:00:00`).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </p>
                                    <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest truncate">
                                      {c.tipo} {c.numero !== '-' ? `· Nº ${c.numero}` : ''}
                                      {c.estado && <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[7px] font-black ${c.estado === 'Pagada' ? 'bg-emerald-100 text-emerald-600' :
                                          c.estado === 'Pendiente' ? 'bg-amber-100 text-amber-600' :
                                            'bg-slate-100 text-slate-500'
                                        }`}>{c.estado}</span>}
                                    </p>
                                    {c.fecha && c.fechaVencimiento && c.fecha !== c.fechaVencimiento && (
                                      <p className="text-[8px] font-bold text-slate-300 uppercase tracking-widest truncate">
                                        Emitida {new Date(`${c.fecha}T12:00:00`).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-[10px] font-black text-rose-500 shrink-0 ml-3">$ {Number(c.monto).toLocaleString('es-AR')}</span>
                              </div>
                            ))}
                            {/* Subtotal del proveedor */}
                            <div className="flex justify-between items-center px-5 py-2 bg-rose-50/50">
                              <span className="text-[8px] font-black text-rose-400 uppercase tracking-widest">Total {prov.name}</span>
                              <span className="text-[10px] font-black text-rose-600">$ {Number(prov.total).toLocaleString('es-AR')}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
              ) : (
                <div className="h-full flex flex-col items-center justify-center opacity-20 gap-3 py-10">
                  <RefreshCcw size={40} />
                  <p className="text-[10px] font-black uppercase tracking-widest">Sin vencimientos en el período</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[450px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-amber-50/70">
              <h3 className="text-[10px] font-black text-amber-700 uppercase tracking-widest">Gastos</h3>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-black text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">{groupedExpenses.length} grupos</span>
                <Building2 size={16} className="text-amber-500 opacity-50" />
              </div>
            </div>
            <div className="p-4 space-y-2 flex-1 overflow-y-auto custom-scrollbar">
              {groupedExpenses.length > 0 ? (
                groupedExpenses.map((group) => (
                  <div key={group.id} className="rounded-2xl border border-amber-100 overflow-hidden bg-white">
                    <div className="flex items-center justify-between px-4 py-3 bg-amber-50/60 border-b border-amber-100">
                      <div className="min-w-0">
                        <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest">{group.label}</p>
                        <p className="text-[8px] font-bold text-amber-500 uppercase tracking-widest">{group.items.length} concepto{group.items.length !== 1 ? 's' : ''}</p>
                      </div>
                      <span className="text-[11px] font-black text-amber-700 shrink-0">- $ {Number(group.totalFiltered).toLocaleString('es-AR')}</span>
                    </div>

                    <div className="divide-y divide-amber-50">
                      {group.items.map((item) => (
                        <div key={item.key} className="flex items-center justify-between px-4 py-3 hover:bg-amber-50/40 transition-colors">
                          <div className="min-w-0 pr-3">
                            <p className="text-[10px] font-black text-slate-600 uppercase truncate">{item.name}</p>
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{item.count} registro{item.count !== 1 ? 's' : ''}</p>
                          </div>
                          <span className="text-[10px] font-black text-amber-700 shrink-0">- $ {Number(item.total || 0).toLocaleString('es-AR')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-full flex flex-col items-center justify-center opacity-20 gap-3 py-10">
                  <Building2 size={40} />
                  <p className="text-[10px] font-black uppercase tracking-widest">Sin gastos</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Movimientos de Analisis segun el toggle: con el proyectado apagado, los tres
  // paneles y los KPIs quedan mostrando lo mismo (solo lo realmente movido).
  const analisisMovementsVisibles = useMemo(() => (
    includeProjected
      ? (analisisMovements || [])
      : (analisisMovements || []).filter((m) => m.state !== 'proyectado')
  ), [analisisMovements, includeProjected]);

  const ingresosClientes = useMemo(() => (
    groupByEntity(analisisMovementsVisibles, 'in').map((group) => ({
      id: group.id,
      name: group.name,
      total: group.total,
      ventas: group.items.map((it) => ({
        id: it.id,
        fecha: it.fecha,
        amount: it.amount,
        label: it.label,
      })),
    }))
  ), [analisisMovementsVisibles]);

  const egresosProveedores = useMemo(() => {
    const provMap = {};
    analisisMovementsVisibles
      .filter((m) => m.source === 'compra')
      .forEach((m) => {
        const provId = m.entityId || '__sin_proveedor__';
        if (!provMap[provId]) {
          provMap[provId] = { id: provId, name: m.entityName || 'Sin Proveedor', total: 0, compras: [] };
        }
        provMap[provId].total += m.amount;
        provMap[provId].compras.push({
          id: m.sourceRowId,
          fecha: m.meta?.emissionDate || m.date,
          fechaVencimiento: m.meta?.dueDate || m.date,
          tipo: m.meta?.voucherType || 'Compra',
          numero: m.meta?.voucherNumber || '-',
          monto: m.amount,
          estado: m.meta?.status,
        });
      });
    return Object.values(provMap)
      .sort((a, b) => b.total - a.total)
      .map((p) => ({
        ...p,
        compras: p.compras.sort((a, b) => {
          const dueDiff =
            new Date(`${a.fechaVencimiento || a.fecha}T12:00:00`).getTime() -
            new Date(`${b.fechaVencimiento || b.fecha}T12:00:00`).getTime();
          if (dueDiff !== 0) return dueDiff;
          return new Date(`${a.fecha}T12:00:00`).getTime() - new Date(`${b.fecha}T12:00:00`).getTime();
        }),
      }));
  }, [analisisMovementsVisibles]);

  const gastosAnalisis = useMemo(() => buildExpenseAnalysisGroups(
    analisisMovementsVisibles
      .filter((m) => m.direction === 'out' && m.source !== 'compra')
      .map((m) => ({ categoria: m.category, concepto: m.concept, monto: m.amount })),
  ), [analisisMovementsVisibles]);

  const currentMonthTotals = useMemo(() => {
    // ============================================================
    // VISTA ANÁLISIS (mes-actual / detalle-mes)
    // ============================================================
    // En esta vista NO se carga dataMesCalendario (solo se llena en la vista
    // Calendario). Antes los KPIs salían en $0 o quedaban con valores viejos
    // porque seguían leyendo dataMesCalendario que estaba vacío. Acá los
    // calculamos directamente desde los datos que sí se cargan en Análisis:
    //   - Ingresos = sumatoria de "Fuentes de Ingreso" (ventas + cuentas por
    //     cobrar + liquidaciones CRM + ingresos bancarios)
    //   - Egresos  = sumatoria de "Compras y Vencimientos" + sumatoria de
    //     "Gastos" del sistema (mismo total que el usuario ve en las columnas).
    if (viewMode === 'mes-actual' || viewMode === 'detalle-mes') {
      const tIn = (ingresosClientes || []).reduce(
        (acc, c) => acc + normalizeMoney(c.total),
        0,
      );
      const tOutCompras = (egresosProveedores || []).reduce(
        (acc, p) => acc + normalizeMoney(p.total),
        0,
      );
      const tOutGastos = (gastosAnalisis || []).reduce(
        (acc, g) => acc + normalizeMoney(g.total),
        0,
      );
      const tOut = tOutCompras + tOutGastos;
      // Los paneles ya vienen filtrados por el toggle, asi que el KPI sigue
      // siendo exactamente la suma de lo que se ve abajo en los dos modos.
      return { tIn, tOut, net: tIn - tOut };
    }

    // ============================================================
    // VISTA CALENDARIO (default): mantenemos la lógica original
    // ============================================================
    let tIn = 0;
    let tOut = 0;
    Object.values(dataMesCalendario).forEach((d) => {
      tIn += normalizeMoney(d.ingresos) + (includeProjected ? normalizeMoney(d.ingProj) : 0);
      tOut += normalizeMoney(d.egresos) + (includeProjected ? normalizeMoney(d.gasProj) : 0);
    });
    return { tIn, tOut, net: tIn - tOut };
  }, [viewMode, dataMesCalendario, ingresosClientes, egresosProveedores, gastosAnalisis, includeProjected]);

  return (
    <div className="imp-page imp-container animate-nova-in">

      {/* HEADER TIPO SISTEMA */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <BarChart3 size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Flujo de Fondos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Control de liquidez, proyecciones y auditoría bancaria.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          {/* Toggle de proyectado: afecta solo a los KPIs de arriba. */}
          <button
            type="button"
            onClick={() => setIncludeProjected((current) => !current)}
            title={includeProjected
              ? 'Los KPIs incluyen cuentas por cobrar, vencimientos y liquidaciones CRM todavia no cobradas'
              : 'Los KPIs muestran solo movimientos ya realizados'}
            className="px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all bg-[#51A7FD] text-white hover:bg-[#3d93e8]"
          >
            {includeProjected ? <Eye size={16} /> : <EyeOff size={16} />}
            {includeProjected ? 'Incluye proy.' : 'Solo real'}
          </button>

          {/* Navegación de Vistas */}
          <button
            onClick={() => activateSection('calendario')}
            className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all
                ${currentSection === 'calendario' ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:bg-slate-800'}`}
          >
            <LayoutGrid size={16} /> Calendario
          </button>

          <button
            onClick={() => activateSection('analisis')}
            className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all
                ${currentSection === 'analisis' ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:bg-slate-800'}`}
          >
            <PieChart size={16} /> Análisis
          </button>

          <button
            onClick={() => activateSection('historial')}
            className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all
                ${currentSection === 'historial' ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:bg-slate-800'}`}
          >
            <List size={16} /> Historial
          </button>

          <button
            onClick={() => activateSection('bancos')}
            className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all
                ${currentSection === 'bancos' ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:bg-slate-800'}`}
          >
            <CreditCard size={16} /> Bancos
          </button>
        </div>
      </div>

      {/* KPI SECTION */}
      {(() => {
        // Calendario y Analisis corren el MISMO motor (getFlowMovements) con los
        // mismos filtros: ninguna de las dos excluye lo proyectado. Rotular el
        // saldo de Analisis como "Real" era incorrecto — mostraba el mismo
        // numero que Calendario llama "Proyectado". Se unifican los rotulos
        // para no sugerir una diferencia de criterio que no existe.
        const kpiSourceLabel = includeProjected ? 'Real + Proy.' : 'Solo real';
        const netLabel = includeProjected ? 'Proyectado' : 'Real';
        return (
          <div className="imp-kpis mb-3">
            <div className="imp-kpi">
              <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ingresos Mes</p>
                <ArrowUpCircle size={16} className="text-[#51A7FD]" />
              </div>
              <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
                $ {currentMonthTotals.tIn.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600">{kpiSourceLabel}</p>
            </div>

            <div className="imp-kpi">
              <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Egresos Mes</p>
                <ArrowDownCircle size={16} className="text-[#F05A57]" />
              </div>
              <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
                $ {currentMonthTotals.tOut.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-600">{kpiSourceLabel}</p>
            </div>

            <div className="imp-kpi">
              <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Saldo Neto</p>
                <Wallet size={16} className="text-emerald-500" />
              </div>
              <p className={`mt-1.5 text-2xl font-black tracking-tight ${currentMonthTotals.net < 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                $ {currentMonthTotals.net.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">{netLabel}</p>
            </div>

            {(() => {
              // % Rentabilidad del flujo = Saldo Neto / Ingresos del mes.
              // Indica qué proporción de los ingresos queda como flujo neto.
              // Sin ingresos no hay base de cálculo (evitamos división por cero).
              const tieneBase = currentMonthTotals.tIn > 0;
              const rentPct = tieneBase
                ? (currentMonthTotals.net / currentMonthTotals.tIn) * 100
                : 0;
              const rentColor = !tieneBase
                ? 'text-slate-400'
                : rentPct < 0
                  ? 'text-rose-600'
                  : 'text-slate-800';
              return (
                <div className="imp-kpi">
                  <div className="flex items-center justify-between">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">% Rentabilidad</p>
                    <Percent size={16} className="text-[#3FF2AA]" />
                  </div>
                  <p className={`mt-1.5 text-2xl font-black tracking-tight ${rentColor}`}>
                    {tieneBase
                      ? `${rentPct.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
                      : '—'}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">
                    Neto sobre ingresos
                  </p>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* BARRA DE HERRAMIENTAS Y FILTROS */}
      <div className="bg-white rounded-2xl border border-[#51A7FD] shadow-sm p-4 mb-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">

          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-64" ref={sitePickerRef}>
              <button
                onClick={() => setShowSitePicker((v) => !v)}
                className="w-full bg-white border border-slate-200 hover:border-slate-300 text-slate-700 px-4 py-2.5 rounded-2xl font-black text-[9px] uppercase tracking-widest shadow-sm transition-all flex items-center justify-between gap-2"
              >
                <span className="flex items-center gap-2"><Building2 size={14} /> {selectedSiteLabel}</span>
                <ChevronDown size={14} className="text-slate-300" />
              </button>
              {showSitePicker && <SitePicker />}
            </div>

            <div className="relative" ref={pickerRef}>
              {viewMode !== 'tabla' && viewMode !== 'nuevo-ingreso-proy' && viewMode !== 'nuevo-gasto-proy' && viewMode !== 'bancos' && viewMode !== 'detalle-banco' && viewMode !== 'detalle-mes' && (
                <div
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-2xl border border-slate-200 cursor-pointer hover:border-[#51A7FD] relative transition-all min-w-[180px] justify-between shadow-sm"
                >
                  <span className="flex items-center gap-2 text-[10px] font-black text-slate-700 uppercase tracking-[0.2em]"><Calendar size={14} className="text-[#51A7FD]" /> {monthLabel(currentDate)}</span>
                  <ChevronDown size={14} className="text-slate-300" />
                  {showDatePicker && <MonthYearPicker />}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto justify-end">
            <button
              onClick={() => { resetFormProyeccion(); setViewMode('nuevo-ingreso-proy'); }}
              className="bg-[#51A7FD] text-white px-4 py-2.5 rounded-2xl font-black text-[9px] uppercase tracking-widest shadow-md hover:brightness-110 flex items-center gap-2 transition-all border border-[#51A7FD]"
            >
              <Plus size={14} strokeWidth={4} /> Ingreso Proy.
            </button>
            <button
              onClick={() => { resetFormProyeccion(); setViewMode('nuevo-gasto-proy'); }}
              className="bg-[#F05A57] text-white px-4 py-2.5 rounded-2xl font-black text-[9px] uppercase tracking-widest shadow-md hover:brightness-110 flex items-center gap-2 transition-all border border-[#F05A57]"
            >
              <Plus size={14} strokeWidth={4} /> Gasto Proy.
            </button>
            <button
              onClick={() => {
                if (viewMode === 'tabla') fetchAnualData();
                else if (viewMode === 'mes-actual' || viewMode === 'detalle-mes') fetchDetalleMensualAgregado();
                else if (viewMode === 'bancos') fetchBanksData();
                else if (viewMode === 'detalle-banco') fetchBankMovements();
                else {
                  fetchSaldoAcumuladoPrevio();
                  fetchCalendarioData();
                }
              }}
              className="bg-white border border-slate-200 hover:border-slate-300 text-slate-700 px-3 py-2.5 rounded-2xl shadow-sm transition-all flex items-center justify-center"
              title="Refrescar datos"
            >
              <RefreshCcw size={16} />
            </button>
          </div>
        </div>
      </div>

      <main className="w-full">
        {loading ? (
          <div className="h-[50vh] flex flex-col items-center justify-center gap-4 opacity-30 italic">
            <RefreshCcw size={48} className="animate-spin text-[#51A7FD]" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em]">Procesando información financiera...</p>
          </div>
        ) : (
          <>
            {viewMode === 'calendario' && <RenderCalendario />}
            {viewMode === 'nuevo-ingreso-proy' && RenderFormProyeccion('ingreso')}
            {viewMode === 'nuevo-gasto-proy' && RenderFormProyeccion('egreso')}
            {viewMode === 'tabla' && <RenderTablaAnual />}
            {viewMode === 'bancos' && <RenderBancos />}
            {viewMode === 'detalle-banco' && <RenderDetalleBanco />}
            {(viewMode === 'detalle-mes' || viewMode === 'mes-actual') && <RenderDetalleMensualAgregado />}
          </>
        )}
      </main>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }
      `}</style>
    </div>
  );
}
